import {
  useInfiniteQuery, useQueries, useQuery, type InfiniteData, type Query, type UseInfiniteQueryResult, type UseQueryResult,
} from '@tanstack/react-query';
import type { Id, Page } from '@contracts/common';
import type {
  BatchAppliedDTO, BatchDetailDTO, BatchMappingBody, BatchParentBody, BatchJobOptionFor, BatchOptionFor, BatchRowDTO, BatchRowOptionsDTO,
  BatchRowPatchBody, BatchRowPatchedDTO, BatchRowsBody, BatchRowsFilter, IngestCreateBody, IngestCreatedDTO,
  IngestJobDTO, IngestJobState, RecordSourcesBody, RecordSourcesDTO,
} from '@contracts/ingest';
import { SOURCES_RESOLVE_MAX } from '@contracts/ingest';
import { api, ApiClientError } from '../../../api/client';

/**
 * The "Luồng bóc" client (S5 §12): one typed call per batch route. Every key sits under `['assistant']`, so the
 * panel's invalidations reach them; every write sends its request id as the Idempotency-Key (the caller keeps the id
 * across a retry of the same intent, as the confirm card does).
 */

export const BATCHES_KEY = ['assistant', 'batches'] as const;
export const batchKey = (jobId: Id) => ['assistant', 'batch', jobId] as const;
/** The page and the card read different filters of one job: both the job and the filter are in the key. */
export const batchRowsKey = (jobId: Id, filter: Omit<BatchRowsFilter, 'kind'>) =>
  ['assistant', 'batch', jobId, 'rows', filter] as const;
export const pendingBatchesKey = (state: IngestJobState) => [...BATCHES_KEY, state] as const;
export const SOURCES_KEY = ['assistant', 'sources'] as const;

/** §8.1: the card asks again every 2 s while the job reads. */
export const BATCH_POLL_MS = 2000;
const MOVING: readonly IngestJobState[] = ['queued', 'reading', 'proposing'];
/** A held job waits on the person's "Chạy" (§7.7), so it does not poll; nor does a settled one (D97). */
export const batchPollMs = (job: IngestJobDTO | undefined): number | false =>
  job !== undefined && MOVING.includes(job.state) && !job.held ? BATCH_POLL_MS : false;
export const isReading = (job: IngestJobDTO | undefined): boolean => batchPollMs(job) !== false;

/** A job or row id as one path segment (L4 F3): an id from the address bar never reaches another route. */
const seg = encodeURIComponent;

/** The rows route reads `status` and `op` as comma lists, and the page's `cursor`. */
export function rowsPath(jobId: Id, filter: Omit<BatchRowsFilter, 'kind'>, cursor: string | null): string {
  const q = new URLSearchParams();
  if (filter.status !== undefined && filter.status.length > 0) q.set('status', filter.status.join(','));
  if (filter.op !== undefined && filter.op.length > 0) q.set('op', filter.op.join(','));
  if (cursor !== null) q.set('cursor', cursor);
  const s = q.toString();
  return `/api/assistant/batches/${seg(jobId)}${s === '' ? '' : `?${s}`}`;
}

/**
 * One job and its first rows page. Polls while it reads (`batchPollMs`); the interval is recomputed on every answer,
 * so it stops at `ready` and comes back when a write (Chạy, Thử lại) puts the job back to reading. A 404 is final: the
 * job is gone (D127 "Lô này không còn"), no retry.
 */
export function useBatch(jobId: Id | null): UseQueryResult<BatchDetailDTO, ApiClientError> {
  return useQuery<BatchDetailDTO, ApiClientError>(batchQuery(jobId));
}

const batchQuery = (jobId: Id | null) => ({
  queryKey: batchKey(jobId ?? ''),
  queryFn: () => api<BatchDetailDTO>(rowsPath(jobId ?? '', {}, null)),
  enabled: jobId !== null,
  refetchInterval: (q: Query<BatchDetailDTO, ApiClientError>) => batchPollMs(q.state.data?.job),
  retry: (n: number, e: ApiClientError) => e.status !== 404 && n < 2,
});

/**
 * §8.5: the actions that wrote the conversation's batches. Their undo is the card's own "Hoàn tác", so the log draws
 * no second undo card for them. The same queries as the cards (one fetch each, shared).
 */
export function useBatchActions(jobIds: readonly Id[]): ReadonlySet<Id> {
  return useQueries({
    queries: jobIds.map((id) => batchQuery(id)),
    combine: (results) => new Set(results.flatMap((r) => (r.data?.job.actionId == null ? [] : [r.data.job.actionId]))),
  });
}

/** The rows of one filter, page after page (`ROWS_PAGE` = 100 a page), by the server's cursor. */
export function useBatchRows(jobId: Id, filter: Omit<BatchRowsFilter, 'kind'>, enabled = true):
  UseInfiniteQueryResult<InfiniteData<Page<BatchRowDTO>, string | null>, ApiClientError> {
  return useInfiniteQuery<Page<BatchRowDTO>, ApiClientError, InfiniteData<Page<BatchRowDTO>, string | null>,
    ReturnType<typeof batchRowsKey>, string | null>({
    queryKey: batchRowsKey(jobId, filter),
    queryFn: async ({ pageParam }) => (await api<BatchDetailDTO>(rowsPath(jobId, filter, pageParam))).rows,
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

/** §8.4: the person's batches in one state (`ready` = "Lô chờ duyệt"), newest first. */
export function usePendingBatches(enabled: boolean, state: IngestJobState = 'ready'):
  UseQueryResult<IngestJobDTO[], ApiClientError> {
  return useQuery<IngestJobDTO[], ApiClientError>({
    queryKey: pendingBatchesKey(state),
    queryFn: () => api<IngestJobDTO[]>(`/api/assistant/batches?state=${state}`),
    enabled,
  });
}

/**
 * D115: the documents behind the records a detail surface shows — one query for the whole surface, sent as one call
 * per `SOURCES_RESOLVE_MAX` refs (a partner with 120 contacts is 3 calls, never 120), the answers merged.
 */
export function useSources(refs: RecordSourcesBody['refs']): UseQueryResult<RecordSourcesDTO, ApiClientError> {
  return useQuery<RecordSourcesDTO, ApiClientError>({
    queryKey: [...SOURCES_KEY, refs],
    queryFn: async () => {
      const chunks: RecordSourcesBody['refs'][] = [];
      for (let at = 0; at < refs.length; at += SOURCES_RESOLVE_MAX) chunks.push(refs.slice(at, at + SOURCES_RESOLVE_MAX));
      const answers = await Promise.all(chunks.map((chunk) =>
        api<RecordSourcesDTO>('/api/assistant/sources/resolve', { method: 'POST', body: { refs: chunk } })));
      return { items: answers.flatMap((a) => a.items) };
    },
    enabled: refs.length > 0,
    staleTime: 60_000,
  });
}

export const startIngest = (body: IngestCreateBody, requestId: Id): Promise<IngestCreatedDTO> =>
  api<IngestCreatedDTO>('/api/assistant/ingest', { method: 'POST', body, requestId });

export const patchRow = (jobId: Id, rowId: Id, body: BatchRowPatchBody, requestId: Id): Promise<BatchRowPatchedDTO> =>
  api<BatchRowPatchedDTO>(`/api/assistant/batches/${seg(jobId)}/rows/${seg(rowId)}`, { method: 'PATCH', body, requestId });

export const bulkSelect = (jobId: Id, body: BatchRowsBody, requestId: Id): Promise<IngestJobDTO> =>
  api<IngestJobDTO>(`/api/assistant/batches/${seg(jobId)}/rows`, { method: 'PATCH', body, requestId });

export const putMapping = (jobId: Id, body: BatchMappingBody, requestId: Id): Promise<IngestJobDTO> =>
  api<IngestJobDTO>(`/api/assistant/batches/${seg(jobId)}/mapping`, { method: 'PUT', body, requestId });
/** D152: the card an unanchored batch's top cards go under ("Đặt dưới"), or null for "Việc lẻ". */
export const putParent = (jobId: Id, body: BatchParentBody, requestId: Id): Promise<IngestJobDTO> =>
  api<IngestJobDTO>(`/api/assistant/batches/${seg(jobId)}/parent`, { method: 'PUT', body, requestId });

const verb = (name: 'run' | 'cancel' | 'discard' | 'retry') => (jobId: Id, version: number, requestId: Id) =>
  api<IngestJobDTO>(`/api/assistant/batches/${seg(jobId)}/${name}`, { method: 'POST', body: { version }, requestId });
export const runBatch = verb('run');
export const cancelBatch = verb('cancel');
export const discardBatch = verb('discard');
export const retryBatch = verb('retry');

export const applyBatch = (jobId: Id, version: number, requestId: Id): Promise<BatchAppliedDTO> =>
  api<BatchAppliedDTO>(`/api/assistant/batches/${seg(jobId)}/apply`, { method: 'POST', body: { version }, requestId });

/** P2: what a row may point at, visible only, ≤ 20. */
export function rowOptions(jobId: Id, rowId: Id, kind: BatchOptionFor, q?: string): Promise<BatchRowOptionsDTO> {
  const s = new URLSearchParams({ for: kind });
  if (q !== undefined && q !== '') s.set('q', q);
  return api<BatchRowOptionsDTO>(`/api/assistant/batches/${seg(jobId)}/rows/${seg(rowId)}/options?${s.toString()}`);
}

/** D139: the batch's own default project or categories (the mapping bar), no row needed; a category in `direction`. */
export function jobOptions(jobId: Id, kind: BatchJobOptionFor, direction?: 'in' | 'out',
  q?: string): Promise<BatchRowOptionsDTO> {
  const s = new URLSearchParams({ for: kind });
  if (direction !== undefined) s.set('direction', direction);
  if (q !== undefined && q !== '') s.set('q', q);
  return api<BatchRowOptionsDTO>(`/api/assistant/batches/${seg(jobId)}/options?${s.toString()}`);
}

/** §11.2: a 60 s link to the evidence file; `inline` opens a PDF or an image in the tab. */
export const openDocument = (documentId: Id, inline = false): Promise<{ url: string; expiresAt: string }> =>
  api<{ url: string; expiresAt: string }>(`/api/assistant/documents/${documentId}/download`,
    { method: 'POST', body: { inline } });
